import { useEffect, useState } from "react";
import { Gift, Sparkles, IndianRupee } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import api, { errorMessage } from "@/lib/api";
import { LoanIcon, findLoan, loanRangeLabel, clampAmount } from "@/lib/loanUtils";
import useLocations from "@/hooks/useLocations";

export default function ReferralButton({ onDone }) {
  const [bonus, setBonus] = useState(500);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", phone: "", district: "", loan_type: "", amount: "", referred_from: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [loans, setLoans] = useState([]);
  const { districts } = useLocations();

  useEffect(() => {
    api.get("/settings/referral").then(r => setBonus(r.data.referral_bonus_amount)).catch(() => {});
    // Loan types come from Admin → Loans master. Fetches on mount so any
    // admin add/rename/reorder is picked up on the next open of this modal.
    api.get("/loans").then(r => setLoans(r.data || [])).catch(() => setLoans([]));
  }, []);

  const submit = async () => {
    if (!f.name || f.phone.length !== 10 || !f.district || !f.loan_type || !f.amount) {
      return toast.error("Fill all required fields correctly");
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/staff/referral-lead", { ...f, amount: parseFloat(f.amount) });
      toast.success(`Referral created: ${data.lead_id}. Bonus if disbursed: ₹${data.referral_bonus_amount}`);
      if (data.whatsapp_url) {
        window.open(data.whatsapp_url, "_blank", "noopener,noreferrer");
      }
      setOpen(false);
      setF({ name: "", phone: "", district: "", loan_type: "", amount: "", referred_from: "", notes: "" });
      onDone && onDone();
    } catch (e) { toast.error(errorMessage(e)); }
    setSubmitting(false);
  };

  return (
    <>
      <div className="w-full rounded-2xl bg-[#141A2E] border border-[#C5A028]/25 p-4 mb-3 box-border" data-testid="referral-banner">
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 rounded-xl bg-[#FF8C00]/15 border border-[#FF8C00]/40 flex items-center justify-center shrink-0">
            <Gift className="w-5 h-5 text-[#FF8C00]"/>
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[11px] uppercase tracking-widest text-[#C5A028] font-bold">Referral Program</div>
            <div className="text-white text-sm mt-1 leading-snug break-words">
              Refer a customer — if disbursed, you get <span className="text-[#FF8C00] font-bold">₹{Number(bonus).toLocaleString("en-IN")}</span> extra bonus!
            </div>
          </div>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-referral-btn" className="mt-3 w-full h-11 bg-[#FF8C00] hover:bg-[#e77e00] text-slate-900 font-bold rounded-full">
              <Sparkles className="w-4 h-4 mr-2"/>Add Reference Customer <span className="ml-2 opacity-70">+ ₹{Number(bonus).toLocaleString("en-IN")}</span>
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-slate-900 border-slate-800 text-white max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Add Reference Customer</DialogTitle>
              <DialogDescription className="sr-only">
                Refer a customer for a loan and earn a bonus if the loan gets disbursed.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <Input data-testid="ref-name" placeholder="Customer name" value={f.name} onChange={e=>setF({...f, name: e.target.value})} className="bg-slate-950 border-slate-700"/>
              <Input data-testid="ref-phone" placeholder="Phone (10 digits)" value={f.phone} maxLength={10} onChange={e=>setF({...f, phone: e.target.value.replace(/\D/g,"")})} className="bg-slate-950 border-slate-700"/>
              <Select value={f.district} onValueChange={v=>setF({...f, district: v})}>
                <SelectTrigger data-testid="ref-district" className="bg-slate-950 border-slate-700"><SelectValue placeholder="District"/></SelectTrigger>
                <SelectContent className="bg-slate-900 text-white border-slate-700 max-h-64 min-w-[--radix-select-trigger-width]">
                  {districts.length === 0 ? <div className="p-3 text-sm text-slate-400">No districts configured. Contact admin.</div> : districts.map(d => <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={f.loan_type} onValueChange={v=>setF({...f, loan_type: v})}>
                <SelectTrigger data-testid="ref-loan-type" className="bg-slate-950 border-slate-700"><SelectValue placeholder="Loan type"/></SelectTrigger>
                <SelectContent className="bg-slate-900 text-white border-slate-700 max-h-64 min-w-[--radix-select-trigger-width]">
                  {loans.length === 0
                    ? <div className="p-3 text-sm text-slate-400">No loans configured. Contact admin.</div>
                    : loans.map(l => <SelectItem key={l.id || l.name} value={l.name}><span className="inline-flex items-center gap-2"><LoanIcon loan={l} className="w-4 h-4 text-amber-500"/>{l.name}</span></SelectItem>)}
                </SelectContent>
              </Select>
              <div>
                <div className="relative">
                  <IndianRupee className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500"/>
                  <Input
                    data-testid="ref-amount"
                    placeholder="Loan amount"
                    value={f.amount}
                    onChange={e=>setF({...f, amount: e.target.value.replace(/\D/g,"")})}
                    onBlur={()=>{ const c = clampAmount(f.amount, findLoan(loans, f.loan_type)); if (c.changed) { setF({...f, amount: c.value}); toast.message(c.why); } }}
                    className="bg-slate-950 border-slate-700 pl-9"/>
                </div>
                {f.loan_type && loanRangeLabel(findLoan(loans, f.loan_type)) && (
                  <div className="mt-1 text-[11px] text-amber-300/80 font-mono-alt" data-testid="ref-amount-range">{loanRangeLabel(findLoan(loans, f.loan_type))}</div>
                )}
              </div>
              <div>
                <Label className="text-slate-400 text-xs uppercase tracking-widest">Referred From (Existing customer / Case ID)</Label>
                <Input data-testid="ref-from" placeholder="e.g. SHC2026ABC123 or Ravi Kumar" value={f.referred_from} onChange={e=>setF({...f, referred_from: e.target.value})} className="bg-slate-950 border-slate-700 mt-2"/>
              </div>
              <div>
                <Label className="text-slate-400 text-xs uppercase tracking-widest">Notes</Label>
                <Textarea data-testid="ref-notes" placeholder="Optional notes" value={f.notes} onChange={e=>setF({...f, notes: e.target.value})} className="bg-slate-950 border-slate-700 mt-2"/>
              </div>
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-sm text-amber-300">
                <strong>₹{Number(bonus).toLocaleString("en-IN")}</strong> bonus will be credited to you if this customer's loan gets disbursed.
              </div>
              <Button data-testid="ref-submit" disabled={submitting} onClick={submit} className="w-full bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold rounded-full">Submit Referral</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
}
